import React, { useState, useEffect, useRef, memo } from 'react';


interface EditableNumberInputProps { 
    initialValue: number | string | null | undefined;
    onChange: (itemId: string, field: string, value: string) => void;
    itemId: string;
    field: string;
    isEditable: boolean;
    activeInputId: string | null;
    setActiveInputId: (id: string | null) => void;
}

export const EditableNumberInput = memo(({
    initialValue,
    onChange,
    itemId,
    field,
    isEditable,
    activeInputId,
    setActiveInputId,
}: EditableNumberInputProps) => {
    const [value, setValue] = useState(initialValue?.toString() ?? '');
    const inputRef = useRef<HTMLInputElement>(null);
    const isTyping = useRef(false);

    // Sync external changes only if not typing
    useEffect(() => {
        if (!isTyping.current) {
            setValue(initialValue?.toString() ?? '');
        }
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        // Allow numbers + optional single decimal
        if (/^\d*\.?\d*$/.test(raw)) {
            isTyping.current = true;
            setValue(raw); // Local state only
        }
    };

    const handleBlur = () => {
        let cleaned = value;

        // Default to 0 if empty or invalid
        if (cleaned === '' || cleaned === '.') {
            cleaned = '0';
        }

        // Final formatting on blur
        const num = parseFloat(cleaned);
        const finalValue = isNaN(num) ? '0' : num.toString();

        isTyping.current = false;
        setValue(finalValue); // Update input display
        onChange(itemId, field, finalValue); // Inform parent
        setActiveInputId(null);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setActiveInputId(`${itemId}-${field}`);
    };

    return isEditable ? (
        <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            className="editable-input input-wrap"
        />
    ) : (
        <span>{initialValue}</span>
    );
}, (prev, next) =>
    prev.initialValue === next.initialValue &&
    prev.isEditable === next.isEditable &&
    prev.itemId === next.itemId &&
    prev.field === next.field &&
    prev.activeInputId === next.activeInputId
);




